import { Component, Event, EventEmitter, Listen, Prop, State } from '@stencil/core';
import {
  post,
  Option,
  PaginationData,
  FacetItem,
  FacetItemChange,
  FacetUpdateEvent,
  FacetChangeEvent,
  DisplayDataUpdateEvent,
  DisplayDataClearEvent,
  ReconnectedEvent,
  FilterPillChangeEvent,
  changeValue,
  clearFilterFromMap,
  getFocusFirstCard,
  updateFacetMap,
  logDebug,
  logError,
  logWarn,
  ClearAllEvent,
  DisplayModalDataUpdateEvent,
  saveAnalyticsWithFacetMap,
  sendSessionErrorData,
  MetaDataIFace,
  ValueType,
  GeoLocationJson,
  geoLocationJsonSearchParameter,
  getRequestParameters,
  setMultiSelectParameter,
  setPaginationParameter,
  setParameter,
  setSingleSelectParameter,
  setSingleSelectParameterToArray,
  getFacetMapWithResponse,
  addAvailabilityOption,
  addGenderOption,
  addNameKeyOption,
  cleanValue,
  geoLocationJsonResponseOverride,
  getFacetOptionsFromResponse,
  geoLocationJsonUrlOverride,
  geoLocationJsonUrlParamOverride,
  getCurrentPath,
  UrlUtils_getFacetMapFromUrl,
  setUrlHistoryWithFacetMap,
  updateUrlHistory,
  ignoreUrlOverride,
  NextAvailable,
  getNextAvailable,
  isNextAvailableSelected,
  DOCTOR_LISTING_SESSION,
  getCookie,
  setSessionCookie,
  fetchPractitionersLocation,
  fetchPractitionerNextAvailable,
  NextAvailableDataUpdateEvent,
  NextAvailableSlotsEvent,
  SlotSearchRequest,
  NextAvailableUpdateEvent,
  BridgeStateManager,
  BridgeStateEvent,
  BridgeUpdateEvent,
  BridgeCloseEvent,
  LaunchBridgeEvent,
  PractitionerSlotData,
  GetBoolean,
  isNully,
  LiveBridgeRules,
  QuestionAnsweredEvent,
  AlertEvent,
  AlertCallback,
  CurrentURLCallback,
  DigitalDataCallback,
  DoctorInfoCallback,
  EmitBridgeCloseEvent,
  EmitBridgeStateEvent,
  StateChangeCallback,
  AnalyticsTracking,
  AnalyticsErrorMessage,
  BridgeErrors,
  PhiManager,
  PhiRule,
  PHIType,
  ModalUpdateEvent,
  NextAvailableSupport,
  appointmentNumberToFilters,
  filtersToAppointmentNumber,
} from '@hpfc/fc-frontend-core';
import { EventManager } from '../../model/EventManager';
const SORT_RECOMMENDED = 'recommended';
const SORT_NEXT_AVAILABLE = 'nextAvailable';
/**
 * This is the central data hub for the Care Practitioner Listing page.
 */
@Component({
  tag: 'fccd-care-practitioners-event-manager',
})
export class CarePractitionersEventManager implements EventManager {
  @Prop() minisiteName?: string;
  @Prop() specialtyName?: string;

  @Event() fccdAlertEvent: EventEmitter<AlertEvent>;
  @Event() fccdBridgeCloseEvent: EventEmitter<BridgeCloseEvent>;
  @Event() fccdBridgeStateEvent: EventEmitter<BridgeStateEvent>;
  @Event() fccdComponentEvent: EventEmitter<FacetUpdateEvent>;
  @Event() fccdDisplayDataUpdateEvent: EventEmitter<DisplayDataUpdateEvent>;
  @Event() fccdDisplayDataClearEvent: EventEmitter<DisplayDataClearEvent>;
  @Event() fccdDisplayModalDataUpdateEvent: EventEmitter<DisplayModalDataUpdateEvent>;
  @Event() fccdNextAvailableDataUpdateEvent: EventEmitter<NextAvailableDataUpdateEvent>;
  @Event() fccdNextAvailableUpdateEvent: EventEmitter<FacetChangeEvent>;
  @Event() questionAnsweredEvent: EventEmitter<QuestionAnsweredEvent>;

  @State() currentValue?: string;
  @State() facetMap: Map<string, FacetItem> = new Map<string, FacetItem>();
  @State() savedFacetMap: Map<string, FacetItem> = new Map<string, FacetItem>();
  @State() facetOptionMap: Map<string, Array<Option>> = new Map<string, Array<Option>>();
  @State() nextAvailable: NextAvailable;
  @State() data: any;
  @State() slotData: any;
  @State() sort: string;
  @State() bridgeManager: BridgeStateManager;
  paramPhiService: PhiManager;
  @State() connected: boolean = false;

  /**
   * Number of practitioners to query for per page.
   */
  @State() size: number = 20;

  @State() abortController: AbortController;
  @State() suppressHistory: boolean;
  @State() clearBridgeParams: boolean;
  nextAvailableData: NextAvailableSupport;
  appointmentTypesHistory: Array<number> = [];

  readonly APPOINTMENT_TYPE_OTHER = 0;

  phiRules: Array<PhiRule> = [
    { name: `${BridgeStateManager.QUESTION_PREFIX}phone-number`, type: PHIType.PHONE },
    { name: `${BridgeStateManager.QUESTION_PREFIX}age-and-status`, type: PHIType.STRING },
    { name: 'patientAgeDays', type: PHIType.NUMBER },
  ];

  facetMetaMap: MetaDataIFace = {
    customPreFilterRules: this.basicCustomFilterRules.bind(this),
    customResponseFilterRules: this.responseOverrideFacetMap.bind(this),
    metaData: [
      {
        componentName: 'name',
        analyticsName: 'docName',
        requestName: 'name',
        requestType: ValueType.string,
        responseName: 'practitionerFilterOptions.applied.name',
        responseType: ValueType.string,
      },
      {
        componentName: 'expertises',
        analyticsName: 'specialty',
        requestName: 'specialty',
        requestType: ValueType.string,
        responseName: 'practitionerFilterOptions.applied.specialty',
        responseType: ValueType.string,
        optionName: 'practitionerFilterOptions.expertises',
        optionType: ValueType.string,
        addRequestOverride: setSingleSelectParameter,
      },
      {
        componentName: 'genders',
        analyticsName: 'genders',
        requestName: 'genders',
        requestType: ValueType.array,
        responseName: 'practitionerFilterOptions.applied.genders',
        responseType: ValueType.array,
        optionName: 'practitionerFilterOptions.genders',
        optionType: ValueType.array,
        addOptionOverride: addGenderOption,
        addRequestOverride: setMultiSelectParameter,
      },
      {
        componentName: 'availability',
        analyticsName: 'schedulingNewPatientsOnly',
        requestName: 'isSchedulingNewPatients',
        requestType: ValueType.string,
        responseName: 'practitionerFilterOptions.applied.isSchedulingNewPatients',
        responseType: ValueType.string,
        optionName: 'practitionerFilterOptions.schedulingNewPatients',
        optionType: ValueType.array,
        addOptionOverride: addAvailabilityOption,
        addRequestOverride: this.setBooleanFromMultiSelectParameter,
      },
      {
        componentName: 'languages',
        analyticsName: 'language',
        requestName: 'languages',
        requestType: ValueType.array,
        responseName: 'practitionerFilterOptions.applied.languages',
        responseType: ValueType.array,
        optionName: 'practitionerFilterOptions.languages',
        optionType: ValueType.array,
        addRequestOverride: setMultiSelectParameter,
      },
      {
        componentName: 'page',
        analyticsName: 'page',
        requestName: 'offset',
        requestType: ValueType.string,
        responseName: 'practitionerFilterOptions.applied.page',
        responseType: ValueType.string,
        addRequestOverride: setPaginationParameter,
        pillBlockDisabled: true,
      },
      {
        componentName: 'types',
        analyticsName: 'type',
        requestName: 'types',
        requestType: ValueType.array,
        responseName: 'practitionerFilterOptions.applied.types',
        responseType: ValueType.array,
        optionName: 'practitionerFilterOptions.types',
        optionType: ValueType.array,
        addRequestOverride: setMultiSelectParameter,
      },
      {
        componentName: 'sort',
        analyticsName: 'sortby',
        requestName: 'sort',
        requestType: ValueType.string,
        responseName: 'practitionerFilterOptions.applied.sort',
        responseType: ValueType.string,
        pillBlockDisabled: true,
        doNotRemoveOnClear: true,
        optionName: 'practitionerFilterOptions.sortBy',
        optionType: ValueType.array,
        addOptionOverride: addNameKeyOption,
      },
      {
        componentName: 'location',
        analyticsName: 'location',
        requestName: 'locations',
        requestType: ValueType.array,
        responseName: 'practitionerFilterOptions.applied.locations',
        responseType: ValueType.array,
        optionName: 'practitionerFilterOptions.locations',
        optionType: ValueType.array,
        addOptionOverride: addNameKeyOption,
        addRequestOverride: setMultiSelectParameter,
      },
      {
        componentName: 'distance',
        analyticsName: 'distance',
        requestName: 'distance',
        requestType: ValueType.string,
        responseName: 'practitionerFilterOptions.applied.distance',
        responseType: ValueType.string,
        optionName: 'practitionerFilterOptions.distanceOptions',
        optionType: ValueType.array,
        addOptionOverride: addNameKeyOption,
        pillBlockDisabled: false,
      },
      {
        componentName: 'locationSearch',
        analyticsName: 'location',
        requestName: undefined,
        requestType: undefined,
        responseName: undefined,
        responseType: undefined,
        addRequestOverride: geoLocationJsonSearchParameter,
        addOptionOverride: null,
        addResponseOverride: geoLocationJsonResponseOverride,
        addUrlOverride: geoLocationJsonUrlOverride,
        addUrlParamOverride: geoLocationJsonUrlParamOverride,
        pillBlockDisabled: true,
      },
      {
        componentName: 'minisiteExpertises',
        analyticsName: 'minisiteExpertises',
        requestName: 'minisiteExpertises',
        requestType: ValueType.array,
        responseName: 'practitionerFilterOptions.applied.minisiteExpertises',
        responseType: ValueType.array,
        optionName: 'practitionerFilterOptions.minisiteExpertises',
        optionType: ValueType.string,
        addRequestOverride: setSingleSelectParameterToArray,
      },
      {
        componentName: 'appointmentTypes',
        analyticsName: 'appointmentTypes',
        requestName: 'nextAvailableFilter.appointmentType',
        requestType: ValueType.string,
        responseName: 'practitionerFilterOptions.applied.nextAvailableFilter.appointmentType',
        responseType: ValueType.array,
        optionName: 'nextAvailableFilterOptions.appointmentTypes',
        optionType: ValueType.array,
        addOptionOverride: addNameKeyOption,
        addRequestOverride: setSingleSelectParameter,
        pillBlockDisabled: true,
      },
      {
        componentName: 'nextAvailableOnly',
        analyticsName: 'nextAvailableTimes',
        requestName: 'nextAvailableFilter.nextAvailableOnly',
        requestType: ValueType.string,
        responseName: 'practitionerFilterOptions.applied.nextAvailableFilter.nextAvailableOnly',
        responseType: ValueType.string,
        optionName: 'nextAvailableFilterOptions.nextAvailableOnly',
        optionType: ValueType.array,
        addOptionOverride: addNameKeyOption,
        addRequestOverride: this.setBooleanFromMultiSelectParameter,
        disableWhen: this.disableNextAvailableOnlyWhen.bind(this),
      },
      {
        componentName: 'patientStatus',
        analyticsName: 'patientStatus',
        requestName: 'nextAvailableFilter.patientStatus',
        requestType: ValueType.string,
        responseName: 'practitionerFilterOptions.applied.nextAvailableFilter.patientStatus',
        responseType: ValueType.string,
        optionName: 'nextAvailableFilterOptions.patientStatus',
        optionType: ValueType.array,
        addOptionOverride: addNameKeyOption,
        disableWhen: this.disableWhenNoPatientStatus.bind(this),
        pillBlockDisabled: true,
        addUrlOverride: ignoreUrlOverride,
      },
      {
        componentName: 'typeOfVisit',
        analyticsName: 'typeOfVisit',
        requestName: 'nextAvailableFilter.typeOfVisit',
        requestType: ValueType.string,
        responseName: 'practitionerFilterOptions.applied.nextAvailableFilter.typeOfVisit',
        responseType: ValueType.string,
        optionName: 'nextAvailableFilterOptions.typeOfVisit',
        optionType: ValueType.array,
        addOptionOverride: addNameKeyOption,
        disableWhen: this.disableWhenNoTypeOfVisit.bind(this),
        pillBlockDisabled: true,
        addUrlOverride: ignoreUrlOverride,
      },
      {
        componentName: 'visitType',
        analyticsName: 'visitType',
        requestName: 'nextAvailableFilter.visitType',
        requestType: ValueType.string,
        responseName: 'practitionerFilterOptions.applied.nextAvailableFilter.visitType',
        responseType: ValueType.string,
        optionName: 'nextAvailableFilterOptions.visitType',
        optionType: ValueType.array,
        addOptionOverride: addNameKeyOption,
        disableWhen: this.disableWhenNoVisitType.bind(this),
        pillBlockDisabled: true,
        addUrlOverride: ignoreUrlOverride,
      },
      {
        componentName: 'patientAgeDays',
        analyticsName: 'patientAgeDays',
        requestName: 'nextAvailableFilter.patientAgeDays',
        requestType: ValueType.string,
        responseName: 'practitionerFilterOptions.applied.nextAvailableFilter.patientAgeDays',
        responseType: ValueType.string,
        pillBlockDisabled: true,
      },
    ],
  };

  /**
   * Captures updates from facets.
   * @param event
   */
  @Listen('fccdFacetChangeEvent', { target: 'document' })
  fccdFacetChangeEventHandler(event: CustomEvent<FacetChangeEvent>) {
    if (event?.detail.facetItem) {
      const updatedFacet = event.detail.facetItem;
      const changed: FacetItemChange = updateFacetMap(this.facetMap, updatedFacet);

      // Only reporting `valueChanged` when the user makes an active selection.
      // Initial load of page will only report entire facet map.
      if (changed && changed.kind === 'valueChanged' && changed.value !== undefined && changed.value !== null) {
        this.currentValue = changed.value.toString();
      }

      logDebug('Control: ' + updatedFacet.controlName + ' data: ' + updatedFacet.value + ' Selected: ' + updatedFacet.selected);

      if (updatedFacet.controlName === 'appointmentTypes' || updatedFacet.controlName === 'expertises') {
        this.clearBridgeParams = true;
      }

      // Resetting the sort type when switching back to not "Other Type"
      // TODO This should be more flexible for future types of appointments
      if (changed.controlName === 'typeOfVisit' && changed.kind == 'valueChanged' && changed.value !== 'other-visit') {
        updateFacetMap(this.facetMap, new FacetItem('sort', SORT_RECOMMENDED));
      }

      //If not page set page to 1.
      if (updatedFacet.controlName != 'page') {
        this.updatePagination(new PaginationData(1, 0, 0, this.size));
      } else {
        (updatedFacet.value as PaginationData).perPage = this.size;
      }

      this.facetMap = this.facetMetaMap.customPreFilterRules(updatedFacet, this.facetMap, this.facetMetaMap, this.nextAvailable, this.facetOptionMap);

      this.fetchCarePractitionerList();
    }
  }

  @Listen('fccdClearAllEvent', { target: 'document' })
  fccdClearAllEventHandler(event: CustomEvent<ClearAllEvent>) {
    logDebug('clear event fired', event);
    this.clearRemovableFacets();
    this.updatePagination(new PaginationData(1, 0, 0, this.size));
    this.fetchCarePractitionerList();
  }

  @Listen('fccdSaveFacetState', { target: 'document' })
  fccdSaveFacetStateEventHandler(event: CustomEvent) {
    logDebug('save state fired', event);
    this.savedFacetMap = new Map(this.facetMap);
  }

  @Listen('fccdLoadFacetState', { target: 'document' })
  fccdLoadFacetStateEventHandler(event: CustomEvent) {
    logDebug('apply saved state fired', event);
    if (this.savedFacetMap) {
      this.facetMap = new Map(this.savedFacetMap);
      this.savedFacetMap = null;
    }
    this.fetchCarePractitionerList();
  }

  @Listen('fccdFilterPillChangeEvent', { target: 'document' })
  fccdFilterPillChangeEventHandler(event: CustomEvent<FilterPillChangeEvent>) {
    if (event?.detail.facetItem) {
      clearFilterFromMap(this.facetMap, event.detail.facetItem);

      logDebug('Control: ' + event.detail.facetItem.controlName + ' data: ' + event.detail.facetItem.value + ' Selected: ' + event.detail.facetItem.selected);

      // If the last pill was cleared (all facets are empty or are marked to
      // not show a pill) then clear facet values in the same way the "Clear
      // Filters" works by completely resetting the map.
      const shouldClear = [...this.facetMap.values()].every(
        facet => facet.value === null || facet.value === undefined || (Array.isArray(facet.value) && facet.value.length === 0) || facet.pillBlockDisabled,
      );

      // Only clear the facetMap accept those labeled to not remove on clear.
      if (shouldClear) {
        this.clearRemovableFacets();
      }

      this.updatePagination(new PaginationData(1, 0, 0, this.size));
      this.fetchCarePractitionerList();
    }
  }

  private clearRemovableFacets() {
    this.facetMap = new Map([...this.facetMap].filter(([key]) => this.facetMetaMap.metaData.find(m => m.componentName === key)?.doNotRemoveOnClear));
  }

  /**
   * Listen for components looking to reload data, on fccdReconnectedEvent and emits fccdComponentEvent with the update.
   * @param event
   */
  @Listen('fccdReconnectedEvent', { target: 'document' })
  fccdReconnectedEventHandler(event: CustomEvent<ReconnectedEvent>) {
    logDebug('Control: ' + event.detail.controlName);
    this.connected = true;
    if (this?.data) {
      const paginationData = CarePractitionersEventManager.getPaginationData(this.data);
      this.fccdDisplayDataUpdateEvent.emit(new DisplayDataUpdateEvent(this.data, paginationData.page, paginationData.pageCount, null));
      this.fccdComponentEvent.emit(new FacetUpdateEvent(this.facetMap, this.facetOptionMap));
      this.updateBridgeManager();
    }
  }

  @State() isBridgeUp: boolean = false;

  /**
   * Listen for components looking to reload data, on fccdReconnectedEvent and emits fccdComponentEvent with the update.
   * @param event
   */
  @Listen('fccdModalUpdateEvent', { target: 'document' })
  practitionersLocationHandler(event: CustomEvent<ModalUpdateEvent>) {
    const location = this.facetMap.get('locationSearch').value as GeoLocationJson;
    fetchPractitionersLocation(event.detail.identifier, this.fccdDisplayModalDataUpdateEvent, location);
  }

  @Listen('fccdNextAvailableUpdateEvent', { target: 'document' })
  practitionerNextAvailableHandler(event: CustomEvent<NextAvailableUpdateEvent>) {
    logDebug('Control: ' + event.detail.data);

    const request: SlotSearchRequest = event.detail.data;

    if (this.facetMap.has('locationSearch')) {
      const location = this.facetMap.get('locationSearch').value as GeoLocationJson;
      request.lng = location?.lng;
      request.lat = location?.lat;
    }

    if (this.facetMap.has('distance')) {
      const distance = this.facetMap.get('distance').value as number;
      request.distance = distance;
    }

    if (this.facetMap.has('appointmentTypes')) {
      const appointmentType = this.facetMap.get('appointmentTypes').value as number;
      request.appointmentType = appointmentType;
    }

    fetchPractitionerNextAvailable(
      this.data,
      request,
      this.fccdNextAvailableDataUpdateEvent,
      (data => {
        this.slotData = data;
      }).bind(this),
    );
  }

  @Listen('fccdNextAvailableSlotsEvent', { target: 'document' })
  practitionerNextAvailableSlotHandler(event: CustomEvent<NextAvailableSlotsEvent>) {
    const request: SlotSearchRequest = event.detail.data;

    if (this.facetMap.has('locationSearch')) {
      const location = this.facetMap.get('locationSearch').value as GeoLocationJson;
      request.lng = location?.lng;
      request.lat = location?.lat;
    }

    if (this.facetMap.has('distance')) {
      const distance = this.facetMap.get('distance').value as number;
      request.distance = distance;
    }

    if (this.facetMap.has('appointmentTypes')) {
      const appointmentType = this.facetMap.get('appointmentTypes').value as number;
      request.appointmentType = appointmentType;
    }

    fetchPractitionerNextAvailable(this.data, request, null, event.detail.callback, true);
  }

  @Listen('popstate', { target: 'window' })
  popstateEventHandler(event: PopStateEvent) {
    logDebug('popStateHandler: ', event);
    if (event.state instanceof Map) {
      this.facetMap = event.state;
    } else {
      // Unable to guarantee the state and URL info will be in sync, easier
      // to reload page based on URL.
      window.location.reload();
      return;
    }

    if (!this.facetMap.has('page')) {
      this.updatePagination(new PaginationData(1, 0, 0, this.size));
    }

    this.suppressHistory = true;
    this.fetchCarePractitionerList();
  }

  @Listen('fccdBridgeUpdateEvent', { target: 'window' })
  handleBridgeUpdateEvent(event: CustomEvent<BridgeUpdateEvent>) {
    logDebug('bridgeupdateevent: ', event);
    this.bridgeManager.handleBridgeUpdateEvent(event.detail);
  }

  /**
   * Listens for an event to launch the bridge.
   */
  @Listen('launchBridgeEvent', { target: 'window' })
  handleLaunchBridgeEvent(event: CustomEvent<LaunchBridgeEvent>) {
    logDebug('launchBridgeEvent: ', event);
    this.bridgeManager.handleLaunchBridgeEvent(event.detail);
  }

  /**
   * Gives other code the ability to access the location href.
   */
  getCurrentURL: CurrentURLCallback = () => {
    return window.location.href;
  };

  /**
   * Method called when the bridge state machine has an update for the over all
   * system.
   *
   * When the bridge manager receives an update from the modal the manager
   * will process the input and update the URL to be something new. Then this
   * method will be called with the updated URL and this class will do the
   * appropriate action.
   *
   * @param data The URL to load
   */
  bridgeCallback: StateChangeCallback = async (data: string) => {
    const newURL = data.replace(/%20/g, '+');
    window.history.replaceState({}, '', newURL);

    // if URL contains `bridge` then trigger the updatedURL rather than loading
    // the system
    const url = new URL(newURL);
    if (url.searchParams.has('bridge')) {
      await this.bridgeManager.updatedURL();
    } else {
      await this.loadSystemFromURL(data);
    }
  };

  /**
   * Fires the event to display an alert.
   */
  alertCallback: AlertCallback = (event: AlertEvent) => {
    this.fccdAlertEvent.emit(event);
  };

  /**
   * Emits the event generated by the BridgeStateManager
   */
  emitBridgeStateEvent: EmitBridgeStateEvent = (event: BridgeStateEvent) => {
    this.fccdBridgeStateEvent.emit(event);
  };

  /**
   * Emits the event generated by the BridgeStateManager
   */
  emitBridgeCloseEvent: EmitBridgeCloseEvent = (event: BridgeCloseEvent) => {
    this.fccdBridgeCloseEvent.emit(event);
  };

  /**
   * Gives other code the ability to access Adobe Analytics
   */
  getDigitalData: DigitalDataCallback = () => {
    return AnalyticsTracking.getDigitalData();
  };

  getSlotsForDoctorInfo = async (practitionerId: number, departmentId: number, appointmentType: number, dateTime: string): Promise<PractitionerSlotData> => {
    const ssr: SlotSearchRequest = new SlotSearchRequest();
    ssr.practitionerIds = [`${practitionerId}`];
    ssr.departmentIds = [departmentId];
    ssr.dateRange = { from: dateTime, to: null };
    ssr.slotLimit = 180;
    ssr.appointmentType = appointmentType;

    const getSlotData: Promise<PractitionerSlotData> = new Promise((res, rej) => {
      fetchPractitionerNextAvailable(this.data, ssr, null, data => res(data), true);

      setTimeout(() => rej(), 1000);
    });

    // If the request fails or rejects after the timeout, return null so we produce an appropriate error
    return await getSlotData.catch(() => {
      return null;
    });
  };

  /**
   * Gives other code the ability to access Doctor Info
   */
  getDoctorInfo: DoctorInfoCallback = async (practitionerId: number, departmentId: number, appointmentType: number, dateTime: string) => {
    if (
      this.slotData &&
      this.slotData.practitionerId === `${practitionerId}` &&
      departmentId &&
      this.slotData.departmentId === `${departmentId}` &&
      dateTime &&
      this.slotData.dateTimes.includes(dateTime)
    ) {
      return this.slotData;
    } else {
      return await this.getSlotsForDoctorInfo(practitionerId, departmentId, appointmentType, dateTime);
    }
  };

  async loadSystemFromURL(url: string) {
    const decryptPhi = await this.decryptPhi(url);

    //Load the parameters
    this.facetMap = UrlUtils_getFacetMapFromUrl(decryptPhi, this.facetMetaMap);

    // Check if the facetMap value for specialty matches the prop
    const matches = this.facetMap.get('expertises')?.value === this.specialtyName;
    if (!matches && !isNully(this.specialtyName)) {
      logDebug(`Specialty in facet doesn't match prop, updating: specialtyName=${this.specialtyName} facet=${this.facetMap.get('expertises')?.value}`);
      this.updateExpertise(cleanValue(this.specialtyName));
    }

    if (!this.facetMap.has('page')) {
      this.updatePagination(new PaginationData(1, 0, 0, this.size));
    } else {
      this.updatePagination(new PaginationData(this.facetMap.get('page').value, 0, 0, this.size));
    }

    //Make the request.
    this.suppressHistory = true;
    this.fetchCarePractitionerList();
  }

  async componentWillLoad() {
    setSessionCookie(DOCTOR_LISTING_SESSION);
    this.paramPhiService = new PhiManager(this.phiRules);
    this.bridgeManager = new BridgeStateManager(
      this.getCurrentURL.bind(this),
      this.bridgeCallback.bind(this),
      this.emitBridgeStateEvent.bind(this),
      this.emitBridgeCloseEvent.bind(this),
      () => {
        return LiveBridgeRules;
      },
      this.getDigitalData.bind(this),
      this.alertCallback.bind(this),
      this.getDoctorInfo.bind(this),
      () => {
        return BridgeErrors;
      },
      this.paramPhiService,
    );

    await this.loadSystemFromURL(window.location.href);
  }

  /**
   * This method creates a URL that is decypted for PHI, that loadSystemFromURL can consume
   * @param href the current real URL.
   */
  async decryptPhi(href: string) {
    const url = new URL(href);
    const keys = Array.from(url.searchParams.keys());
    const urlParams = new URLSearchParams();
    for (let i = 0; i < keys.length; i++) {
      const paramName = keys[i];
      const value = url.searchParams.get(paramName);
      if (this.paramPhiService.isPhi(paramName) && value !== null && value !== undefined && value !== '' && !paramName.startsWith(BridgeStateManager.BRIDGE_PREFIX)) {
        try {
          const result = await this.paramPhiService.getPhi(paramName, value);
          this.paramPhiService.setCurrentGuid(paramName, value);
          urlParams.set(paramName, result.value);
        } catch (e) {
          /** If the URL param cannot be decrypted, exclude it from the facet map */
        }
      } else {
        urlParams.set(paramName, value);
      }
    }
    const baseUrl = url.origin + url.pathname;
    const urlParamsString = urlParams.toString();
    return `${baseUrl}${urlParamsString ? `?${urlParamsString}` : ''}`;
  }

  /**
   * This method creates a copy of the facetMap that has been encrypted for PHI.
   * @param facetMap the current facetMap
   */
  encryptPhi(facetMap: Map<string, FacetItem>) {
    const newMap: Map<string, FacetItem> = new Map();
    const keys = Array.from(facetMap.keys());
    for (let i = 0; i < keys.length; i++) {
      const paramName = keys[i];
      if (this.phiRules.find(x => x.name === paramName)) {
        const facetItem = facetMap.get(paramName);
        if (facetItem.value !== null && facetItem.value !== undefined && facetItem.value !== '') {
          const result = this.paramPhiService.findCurrentGuidByParam(paramName);
          const copy = facetItem.getCopy();
          copy.value = result;
          newMap.set(paramName, copy);
        }
      } else {
        newMap.set(paramName, facetMap.get(paramName).getCopy());
      }
    }
    return newMap;
  }

  /**
   * Updates the URL path (and history) with the current facetMap.
   *
   * When facetMap values change this method should be called to change the URL
   * parameters to match the current state of the page. This method will also
   * update the browser history so that the back button will work as expected.
   *
   * If facetMap does not contain an expertise then we need to check
   * that the currentPath does not include one either. The end of the
   * currentPath (read via getCurrentPath()) should be 'doctors/' or
   * 'doctors/<mini-site-name>/' where `mini-site-name` is alpha and
   * dash only. If no expertise is found a path ending as follows
   * should be changed:
   *
   * When the value `this.minisiteName` exists then the path should end
   * `doctors/<mini-site-name>/` and the expertise should be removed.
   * The minisite name should come from the original URL and not from
   * `this.minisiteName`.
   *
   * When the value `this.minisiteName` does not exist then the path should end
   * `doctors/`.
   *
   * The rest of the path is unknown so the check must be for the end of the url only.
   */
  updatePath() {
    const currentPath = getCurrentPath();

    const encryptedMap = this.encryptPhi(this.facetMap);

    if (!encryptedMap.has('expertise')) {
      // Check if minisiteName is given to the manager, URL's contain 3 tokens:
      // doctor/<mini-site-name>/<expertise>/
      if (this.minisiteName && this.minisiteName.length > 0) {
        // URL should end `doctors/<mini-site-name>/`, check if it
        // contains an expertise and remove it.
        const pathEndsWithExpertise = new RegExp(`/doctors/[a-z-]+/[a-z-]+/$`);
        const newPath = pathEndsWithExpertise.test(currentPath) ? currentPath.replace(/\/[a-z-]+\/$/, '/') : currentPath;
        setUrlHistoryWithFacetMap(encryptedMap, this.facetMetaMap, newPath);
      } else {
        // No minisiteName means URL contains 2 or 3 tokens:
        // doctor/<expertise>/
        // doctor/<expertise>/<subspecialty>/

        // URL should end `doctors/`, remove the variable number of extra tokens
        const newPath = '/care/find/doctors/';

        setUrlHistoryWithFacetMap(encryptedMap, this.facetMetaMap, newPath);
      }
    } else {
      // If expertise exists use the url as currently set
      setUrlHistoryWithFacetMap(encryptedMap, this.facetMetaMap);
    }

    if (this.clearBridgeParams) {
      updateUrlHistory(BridgeStateManager.clearAllParams);
    }

    this.clearBridgeParams = false;
  }

  async fetchCarePractitionerList() {
    const params = getRequestParameters(this.facetMap, this.facetMetaMap);
    const url = '/care/find/api/v2/practitionersPages/';
    this.setupMinisite(params);

    // Prep with required params
    params['size'] = this.size;
    params['useIpLocation'] = true;
    const seed = getCookie('_dyjsession');
    if (seed) {
      params['seed'] = seed;
    }
    if (params['sort'] === undefined) {
      if (this.facetOptionMap.has('sort')) {
        const sortByOptions = this.facetOptionMap.get('sort');
        params['sort'] = sortByOptions[0].value;
      }
    }

    // TODO This should be better connected to BridgeStateManager
    if (this.clearBridgeParams) {
      delete params['patientAgeDays'];
    }

    if (params['nextAvailableFilter'] !== undefined) {
      // To help speed up the SEO results limiting all searches to 4 slots per
      // doctor. The More/Less is handled async.
      params['nextAvailableFilter']['slotLimit'] = 4;
    }

    logDebug(`URL: ${url} `, params);

    this.configureAbortController();

    // Trigger spinner
    this.fccdDisplayDataClearEvent.emit(new DisplayDataClearEvent());

    return post(url, params, this.abortController)
      .then(rsp => {
        // Convert output to new format
        Object.defineProperty(rsp, 'data', Object.getOwnPropertyDescriptor(rsp, 'results'));
        delete rsp['results'];
        return rsp;
      })
      .then(rsp => {
        logDebug('returned: ', JSON.stringify(rsp));
        const results: any = rsp;
        this.data = results;

        const paginationData = CarePractitionersEventManager.getPaginationData(results);

        const isPaginationControl = this.wasPaginationControlSelected('page');

        this.fccdDisplayDataUpdateEvent.emit(new DisplayDataUpdateEvent(rsp, paginationData.page, paginationData.pageCount, null));

        const facetMapWithResponse = getFacetMapWithResponse(results, this.facetMetaMap);

        // Update the nextAvailableData state variable
        this.nextAvailable = getNextAvailable(results.nextAvailable);
        this.nextAvailableData = results.nextAvailableSupport;

        this.facetMetaMap.customResponseFilterRules(this.facetMap, facetMapWithResponse, this.facetMetaMap);

        this.facetMap = facetMapWithResponse;

        this.facetOptionMap = getFacetOptionsFromResponse(results, this.facetMetaMap, this.facetMap, this.facetOptionMap);

        this.updatePagination(paginationData);

        // Either sort is returned in the results or we default to the first sort option.
        if (results.sort) {
          this.sort = results.sort;
        } else {
          if (this.facetOptionMap.has('sort')) {
            const sortByOptions = this.facetOptionMap.get('sort');
            this.sort = sortByOptions[0].value;
          }
        }

        this.fccdComponentEvent.emit(new FacetUpdateEvent(this.facetMap, this.facetOptionMap));

        if (!this.suppressHistory) {
          this.updatePath();
        }

        this.suppressHistory = false;
        this.clearBridgeParams = false;

        if (isPaginationControl) {
          getFocusFirstCard();
        } else {
          // Only want to save on filters.
          saveAnalyticsWithFacetMap('filter_submit', this.facetMap, this.facetMetaMap, getCookie(DOCTOR_LISTING_SESSION), this.currentValue);
          this.currentValue = null;
        }

        return this.updateBridgeManager();
      })
      .catch(e => {
        if (e instanceof DOMException && e.name === 'AbortError') {
          logWarn(e.message, e);
        } else {
          logError(e);
          logDebug('failed to return');
          sendSessionErrorData(AnalyticsErrorMessage.APIFailure, getCookie(DOCTOR_LISTING_SESSION));
          this.fccdDisplayDataUpdateEvent.emit(DisplayDataUpdateEvent.error());
        }
      });
  }

  private updateBridgeManager() {
    if (this.connected && !this.isBridgeUp) {
      // Check with bridge manager to see if anything NA is required
      this.isBridgeUp = true;
      return this.bridgeManager.updatedURL();
    } else {
      return Promise.resolve();
    }
  }

  private setupMinisite(params: object) {
    if (this.minisiteName) {
      params['isMinisite'] = true;
      params['miniSiteTag'] = this.minisiteName;
    } else {
      params['isMinisite'] = false;
    }
  }

  responseOverrideFacetMap(currentFacetMap: Map<string, FacetItem>, responseFacetMap: Map<string, FacetItem>, facetMetaMap: MetaDataIFace): void {
    const hasNoCurrentLocation =
      currentFacetMap?.has('locationSearch') && (!currentFacetMap.get('locationSearch').value || currentFacetMap.get('locationSearch').value.length === 0);

    const isDistanceSelected = currentFacetMap?.get('sort')?.value === 'dist';

    // Check for disabling field
    facetMetaMap.metaData.forEach(meta => {
      if (meta.disableWhen != null) {
        let facet = responseFacetMap.get(meta.componentName);
        facet = facet ? facet : new FacetItem(meta.componentName, null, false);
        facet.disabled = GetBoolean(meta.disableWhen, responseFacetMap);
        updateFacetMap(responseFacetMap, facet);
      }
    });

    // Check for disabling pills
    facetMetaMap.metaData.forEach(meta => {
      if (meta.disableWhen != null) {
        let facet = responseFacetMap.get(meta.componentName);
        facet = facet ? facet : new FacetItem(meta.componentName, null, false);
        facet.pillBlockDisabled = GetBoolean(meta.disableWhen, responseFacetMap);
        updateFacetMap(responseFacetMap, facet);
      }
    });

    // Cleaning up anything that shouldn't be there when no longer doing next available
    if (this.nextAvailable?.hasNextAvailable === false) {
      if (currentFacetMap?.has('nextAvailableOnly')) {
        const facet = currentFacetMap.get('nextAvailableOnly');
        clearFilterFromMap(responseFacetMap, facet);
      }
      if (currentFacetMap?.has('appointmentTypes')) {
        const facet = currentFacetMap.get('appointmentTypes');
        clearFilterFromMap(responseFacetMap, facet);
      }
      if (currentFacetMap?.has('patientAgeDays')) {
        const facet = currentFacetMap.get('patientAgeDays');
        clearFilterFromMap(responseFacetMap, facet);
      }

      // If sort is still set to nextAvailable then change it to name
      if (currentFacetMap?.has('sort') && (currentFacetMap.get('sort').value === SORT_RECOMMENDED || currentFacetMap.get('sort').value === SORT_NEXT_AVAILABLE)) {
        const facet = currentFacetMap.get('sort');
        clearFilterFromMap(responseFacetMap, facet);
      }
    }

    // Figure out the NA Filter facets based on appointmentTypes
    if (this.nextAvailable?.hasNextAvailable === true) {
      // Setup filters for next available
      const facet = responseFacetMap.get('appointmentTypes');
      const expectedFacets = appointmentNumberToFilters(this.nextAvailableData, facet.value);
      for (const [_key, value] of expectedFacets) {
        if (value) {
          // Block the pill and then update the value for the filter
          const pillBlockDisabled = facetMetaMap.metaData.find(e => e.componentName === _key)?.pillBlockDisabled;
          value.pillBlockDisabled = pillBlockDisabled;
          updateFacetMap(responseFacetMap, value);
        }
      }

      // Storing latest appointment type in history, used to figure out how to
      // go from an NA state with less filter values to one that has more.
      if (this.appointmentTypesHistory[0] !== facet.value) {
        this.appointmentTypesHistory.unshift(facet.value);
      }
    }

    if (hasNoCurrentLocation && !isDistanceSelected) {
      const locationSearch = 'locationSearch';
      const currentFacet = currentFacetMap.get(locationSearch);
      const pillBlockDisabled = facetMetaMap.metaData.find(e => e.componentName === locationSearch)?.pillBlockDisabled;
      currentFacet.pillBlockDisabled = pillBlockDisabled;
      updateFacetMap(responseFacetMap, currentFacetMap.get(locationSearch));
    }
  }

  private static getPaginationData(results: any): PaginationData {
    const offset = results.practitionerFilterOptions.applied.offset;
    const size = results.practitionerFilterOptions.applied.size;
    const totalCount = results.totalCount;

    // Generate paging info
    const page: number = Math.ceil(offset / size) + 1;
    const totalPages: number = Math.ceil(totalCount / size);

    return new PaginationData(page, totalPages, totalCount, size);
  }

  private wasPaginationControlSelected(controlName: string) {
    const pageFacet = this.facetMap.get(controlName);
    return (pageFacet?.value as PaginationData).changingPage;
  }

  private configureAbortController() {
    if (this.abortController) {
      this.abortController.abort();
    }
    this.abortController = new AbortController();
  }

  private updateExpertise(expertise: string) {
    const facetItem: FacetItem = new FacetItem('expertises', expertise, true);
    updateFacetMap(this.facetMap, facetItem);
  }

  private updatePagination(paginationData: PaginationData) {
    const facetItem: FacetItem = new FacetItem('page', paginationData);
    facetItem.pillBlockDisabled = true;
    updateFacetMap(this.facetMap, facetItem);
  }

  basicCustomFilterRules(
    facetItem: FacetItem,
    facetItemMap: Map<string, FacetItem>,
    facetMetaMap: MetaDataIFace,
    nextAvailable: NextAvailable,
    facetOptionMap: Map<string, Array<Option>>,
  ): Map<string, FacetItem> {
    if (!facetItem?.controlName) {
      return facetItemMap;
    }

    switch (facetItem.controlName) {
      case 'expertises':
        if (isNextAvailableSelected(facetItem.value, nextAvailable)) {
          changeValue(
            'sort',
            SORT_RECOMMENDED,
            facetItemMap,
            facetMetaMap.metaData.find(e => e.componentName == 'sort'),
          );

          //take the first.
          if (facetOptionMap.has('appointmentTypes') && facetOptionMap.get('appointmentTypes').length > 0) {
            changeValue(
              'appointmentTypes',
              facetOptionMap.get('appointmentTypes')[0].value,
              facetItemMap,
              facetMetaMap.metaData.find(e => e.componentName == 'appointmentTypes'),
            );
          }
        } else {
          //if we are not next avail remove the slot filters from the list.
          if (facetItemMap.has('appointmentTypes')) {
            clearFilterFromMap(facetItemMap, facetItemMap.get('appointmentTypes'));
          }
        }
        break;

      case 'appointmentTypes': {
        // Update Sort
        const sortFacet = facetItemMap.get('sort');
        if (sortFacet && sortFacet.value !== SORT_RECOMMENDED && facetItem.value !== '0') {
          changeValue(
            'sort',
            SORT_RECOMMENDED,
            facetItemMap,
            facetMetaMap.metaData.find(e => e.componentName == 'sort'),
          );
        }

        break;
      }

      // All of the NA Filter options
      case 'typeOfVisit':
      case 'visitType':
      case 'patientStatus': {
        // Check if current setup has a valid appointment type
        const currentAppointmentType = facetItemMap.get('appointmentTypes');
        if (currentAppointmentType !== undefined && currentAppointmentType.value !== 0) {
          const expectedFilters = appointmentNumberToFilters(this.nextAvailableData, currentAppointmentType.value);
          expectedFilters.set(facetItem.controlName, facetItem);
          const newType = filtersToAppointmentNumber(this.nextAvailableData, expectedFilters);

          // Using the current appointment type and swapping out the new filter value looks to match
          if (newType !== null) {
            changeValue(
              'appointmentTypes',
              newType,
              facetItemMap,
              facetMetaMap.metaData.find(e => e.componentName == 'appointmentTypes'),
            );
            break; // Breaks from switch
          }
        }

        // If the current doesn't work, check through past appointment types
        while (this.appointmentTypesHistory.length > 0) {
          const latestType = this.appointmentTypesHistory.shift();
          if (latestType !== undefined) {
            const expectedFilters = appointmentNumberToFilters(this.nextAvailableData, latestType);
            expectedFilters.set(facetItem.controlName, facetItem);
            const newType = filtersToAppointmentNumber(this.nextAvailableData, expectedFilters);

            // Using a previous type and swapping out the new filter value looks to match
            if (newType !== null) {
              changeValue(
                'appointmentTypes',
                latestType,
                facetItemMap,
                facetMetaMap.metaData.find(e => e.componentName == 'appointmentTypes'),
              );
              break; // Breaks from while loop
            }
          }
        }

        // If no previous appointment types work, revert to default
        if (this.appointmentTypesHistory.length === 0) {
          logWarn('Unable to find previous matching appointment type, reverting to default');
          const defaultAppointmentType = this.nextAvailableData.appointmentConfig.find(o => o.defaultSelection)?.appointmentTypeNumber;
          changeValue(
            'appointmentTypes',
            defaultAppointmentType,
            facetItemMap,
            facetMetaMap.metaData.find(e => e.componentName == 'appointmentTypes'),
          );
        }

        break;
      }
    }

    return facetItemMap;
  }

  /**
   * setting chooses value to facetItem from availability selected option
   * @param params
   * @param facetItem
   * @param requestName
   */
  setBooleanFromMultiSelectParameter(params: any, facetItem: FacetItem, requestName?: string): any {
    if (facetItem.value instanceof Array && facetItem.value.length > 0) {
      const value = facetItem.value[0] === 'true';
      params = setParameter(value, params, requestName);
    }
    if (!(facetItem.value instanceof Array)) {
      params = setParameter(facetItem.value, params, requestName);
    }
    return params;
  }

  /**
   * Disables when appointmentType === 0 (other visit type)
   */
  disableNextAvailableOnlyWhen(resFacetMap: Map<string, FacetItem>): boolean {
    const facetItem = resFacetMap.get('appointmentTypes');
    if (facetItem && this.isTypeOfVisitOther(facetItem)) {
      return true;
    }

    return false;
  }

  /**
   * Disables the patientStatus facet when it doesn't fit the appointmentType
   */
  disableWhenNoPatientStatus(resFacetMap: Map<string, FacetItem>): boolean {
    if (!this.nextAvailableData) {
      return false;
    }
    const facetItem = resFacetMap.get('appointmentTypes');
    if (!facetItem || this.isTypeOfVisitOther(facetItem)) {
      return true;
    }
    const expectedConfig = appointmentNumberToFilters(this.nextAvailableData, facetItem.value);
    return !expectedConfig.has('patientStatus');
  }

  /**
   * Disables the typeOfVisit facet when it doesn't fit the appointmentType
   */
  disableWhenNoTypeOfVisit(resFacetMap: Map<string, FacetItem>): boolean {
    if (!this.nextAvailableData) {
      return false;
    }
    const facetItem = resFacetMap.get('appointmentTypes');
    if (!facetItem || this.isTypeOfVisitOther(facetItem)) {
      return true;
    }
    const expectedConfig = appointmentNumberToFilters(this.nextAvailableData, facetItem.value);
    return !expectedConfig.has('typeOfVisit');
  }

  /**
   * Disables the visitType facet when it doesn't fit the appointmentType
   */
  disableWhenNoVisitType(resFacetMap: Map<string, FacetItem>): boolean {
    if (!this.nextAvailableData) {
      return false;
    }
    const facetItem = resFacetMap.get('appointmentTypes');
    if (facetItem && this.isTypeOfVisitOther(facetItem)) {
      return true;
    }
    const expectedConfig = appointmentNumberToFilters(this.nextAvailableData, facetItem.value);
    return !expectedConfig.has('visitType');
  }
  /**
   * Disables the appointmentType facet when it doesn't fit the appointmentType
   */
  isTypeOfVisitOther(facetItem: FacetItem): boolean {
    return facetItem.value === this.APPOINTMENT_TYPE_OTHER || (Array.isArray(facetItem.value) && facetItem.value.includes(this.APPOINTMENT_TYPE_OTHER));
  }
}
